import { Button, Card, Col, Divider, Modal, Row, Table, Tag, Input, DatePicker, Space } from 'antd';
import { FilterOutlined, ReloadOutlined, CloseCircleOutlined, ImportOutlined } from '@ant-design/icons';
import * as React from 'react';
import * as moment from 'moment';
import { adopt } from 'react-adopt';
import MinimalOrders, { OrderFilter } from '../../components/Query/Orders/MinimalOrders';
import Stats from '../../components/Query/Orders/Stats';

import OrderStatesQuery from '../../components/Query/OrderStates';
import DeliveryMethods from '../../components/Query/DeliveryMethods';
import PaymentMethods from '../../components/Query/PaymentMethods';
import { Title } from '../../components/Layout/components';
import PriceDisplay from 'src/components/Others/PriceDisplay';
import CurrenciesQuery from '../../components/Query/Currencies/Currencies';
import ConfiguratorImport from './components/ConfiguratorImport';

const EXCLUDED_ORDER_STATES = ['0f7ceaa3-e07f-4c05-b38b-a5e062f76d41'];

const getDefaultStatuses = (statuses: any) => {
  return statuses
    .filter((eos: any) => EXCLUDED_ORDER_STATES.indexOf(eos.id) === -1)
    .map((eos: any) => eos.id);
}

const OrdersData = adopt({
  deliveryMethods: ({ domain, render }: any) => <DeliveryMethods domain={domain}>{render}</DeliveryMethods>,
  paymentMethods: ({ domain, render }: any) => <PaymentMethods domain={domain}>{render}</PaymentMethods>,
  orderStates: ({ domain, render }: any) => <OrderStatesQuery domain={domain}>{render}</OrderStatesQuery>,
  orders: ({ domain, render, first, skip, filter, customerId, orderStates }: any) => {
    if (orderStates.externalOrderStates.loading) {
      return <></>;
    }

    const { externalOrderStates: { externalOrderStates } } = orderStates;
    const f = { ...filter, orderState: filter.orderState || getDefaultStatuses(externalOrderStates) };

    return (
      <MinimalOrders
        skip={skip}
        first={first}
        filter={f}
        domain={domain}
        customerId={customerId}
      >
        {render}
      </MinimalOrders>
    )
  },

});

interface IOrdersTableState {
  filterParams: OrderFilter;
  filter: OrderFilter | null;
  limit: number;
  selected: string[];
  currentData: any;
  hash: string | null;
}

interface IOrdersTableProps {
  searchQuery: string;
  domain: string;
  customerId?: string;
  filter: any;
  onNewOrder: () => any;
  onEditOrder: (orderId: string) => any;
  onSearch: (params: any) => void;
}

class OrdersTable extends React.Component<IOrdersTableProps, IOrdersTableState> {
  private timeout;

  constructor(props: any) {
    super(props);
    this.state = {
      filterParams: {
        page: 1,
        ...props.filter,
      } as any,
      filter: null,
      limit: 50,
      selected: [],
      currentData: null,

      hash: null,
    };

    this.setFilterParam = this.setFilterParam.bind(this);
    this.setFilter = this.setFilter.bind(this);
  }

  public render() {
    const { hash } = this.state;
    // const hash = '685944c81892a69ce4427b52'; // conversion red, conditional code
    // const hash = 'ef26a89aceea1b2c574b6393'; // BMCL (General code)
    // const hash = '0d482be38d460b38be2669a8'; // Sidemount - EUR vs USD
    // const hash = '42db444cdcc8a18798a491d3'; // Backmount / Light / Black
    // const hash ='0d0b845f6ea89abe6b603f9f'; // Backmount / Heavy / 2x7l
    // const hash = '2db4961e7756f96d1c9a9a0e'; // conversion FMCL
    // hash = 'c261ea66897d793c5f5d5308';
    return (
      <>
        {hash ? <ConfiguratorImport hash={hash} domain={this.props.domain} onClose={() => this.setState({ hash: null })}/> : null}
        <Row>
          <Col span={24}>
            <OrdersData
              skip={this.state.limit * ((this.state.filterParams.page || 1) - 1)}
              first={this.state.limit}
              filter={this.state.filterParams}
              domain={this.props.domain}
              customerId={this.props.customerId}
            >
              {(data: any) => this.renderData(data)}
            </OrdersData>
          </Col>
        </Row>
      </>
    );
  }


  private setFilterParam(name: string, value: any) {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    const page = name === 'page' ? parseInt(value, 10) : 1;
    this.timeout = setTimeout(() => {
      this.setState({
        filterParams: { ...this.state.filterParams, [name]: value, page }
      });

      this.setFilter();
    }, 500);
  };

  private setFilter() {
    this.props.onSearch({ ...this.state.filterParams });
  };

  private getGrandTotal(grandTotal, primaryCurrency) {
    if (grandTotal && grandTotal.currency && grandTotal.currency.exchangeRate && primaryCurrency) {
      if (grandTotal.currency.id === primaryCurrency.id) {
        return grandTotal.grandTotal;
      } else {
        return grandTotal.grandTotal / grandTotal.currency.exchangeRate;
      }
    }
    return 0;
  };

  private getFilterData(data: any[], value: string, text: string) {
    return data.map((v: any) => ({
      value: v[value],
      text: v[text]
    }));
  };

  private getUniqueData(orders: any, path: string) {
    let values = orders.orders.edges.map(i => path.split('.').reduce((a, b) => { return a && a[b] }, i.node));
    let uniqueValues = new Set(values);
    let formattedFilterValues = Array.from(uniqueValues).sort()
    return formattedFilterValues;
  };

  private handleReset(clearFilters) {
    clearFilters();
  };

  private resetAllFilters() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      this.setState({
        filterParams: {
          query: undefined,
          page: 1,
          createdAtFrom: null,
          createdAtTo: null,
          orderAmountFrom: null,
          orderAmountTo: null,
          orderState: null,
          orderPaid: null,
          deliveryMethod: null,
          paymentMethod: null,
        }
      });

      this.setFilter();
    }, 500);
  };

  private renderFilterForm(orderStates, deliveryMethods, paymentMethods, refetch: Function) {
    const { hash } = this.state;
    const inputStyle = { marginRight: '4px', marginBottom: '4px' };
    return (
      <React.Fragment>
        <Row>
          <Col span={20}>
            <Input
              placeholder="Search (order #, email, phone, customer name, street, zip, city, country, package number, tracking link, notes, product name, EAN #, SKU #)"
              defaultValue={this.state.filterParams.query}
              onChange={(e: any) => this.setFilterParam('query', e.target.value)}
              style={{ ...inputStyle, maxWidth: '900px' }}
            />
            <Button onClick={this.setFilter} style={inputStyle}>
              <FilterOutlined />
                Filter
              </Button>
            <Button type="primary" onClick={(e: any) => refetch()} style={inputStyle}>
              <ReloadOutlined />
                Refresh
              </Button>
            <Button onClick={(e: any) => this.resetAllFilters()}>
              <CloseCircleOutlined />
                Reset all filters
              </Button>
          </Col>
          <Col span={4}>
            <Input.Group compact={true}>
              <Input style={{ width: '80%' }} defaultValue={hash || ''} onBlur={(e: any) => this.setState({ hash: e.target.value })} />
              <Button style={{ width: '20%' }} >
                <ImportOutlined />
              </Button>
            </Input.Group>
          </Col>
        </Row>
        <Divider />
      </React.Fragment>
    );
  }

  private renderData(data: any) {

    if ((data.orders && data.orders.orders.loading)
      || (data.deliveryMethods && data.deliveryMethods.loading)
      || (data.paymentMethods && data.paymentMethods.loading)) {
      return null;
    }

    const { deliveryMethods: { deliveryMethods: { deliveryMethods } } } = data;
    const { paymentMethods: { paymentMethods: { paymentMethods } } } = data;
    const { orders: { orders } } = data;
    const { orderStates: { externalOrderStates: { externalOrderStates: orderStates } } } = data;

    if (deliveryMethods && paymentMethods && orders && orderStates) {
      const { orders: { orders } } = data;
      const deleteOrder = () => { };

      return (
        <CurrenciesQuery>
          {({ primaryCurrency }) =>
            <div>
              <Title>Orders</Title>
              <Divider />

              {this.renderFilterForm(orderStates, deliveryMethods, paymentMethods, orders.refetch)}

              <Table
                rowKey={(record: GQL.IOrder) => record.id}
                dataSource={orders.orders.edges.map((e: GQL.IOrderEdge) => e.node)}
                pagination={{
                  total: orders.orders.aggregate.count,
                  current: (parseInt(String(this.state.filterParams.page), 10) || 1),
                  pageSize: this.state.limit,
                  onChange: page => this.setFilterParam('page', parseInt(String(page), 10))
                }}
                onChange={(pagination, filters, sorter, currentDataSource) => this.setState({
                  currentData: currentDataSource.currentDataSource,
                  filterParams: {
                    ...this.state.filterParams,
                    orderState: filters.orderState as string[],
                    orderPaid: filters.orderPaid as string[],
                    deliveryMethod: filters.deliveryMethod as string[],
                    paymentMethod: filters.paymentMethod as string[]
                  }

                })}
                summary={(currentData) => {
                  let allDataAmount: any = [];

                  if (currentData.length < 50 && this.state.filterParams.page == 1) {
                    currentData.map(i => allDataAmount.push([i.subTotal, i.currency && i.currency.value]))
                  } else if (!this.state.currentData) {
                    orders.orders.edges.map(i => {
                      if (i.node.externalState && i.node.externalState.title !== 'Storno') {
                        allDataAmount.push([i.node.subTotal, i.node.currency.value])
                      }
                    })
                  } else {
                    this.state.currentData.map(i => allDataAmount.push([i.subTotal, i.currency.value]))
                  }

                  let totals = {
                    czkTotal: 0,
                    eurTotal: 0,
                    usdTotal: 0
                  }

                  allDataAmount.forEach(i => {
                    if (i[1] === 'CZK') {
                      totals.czkTotal += i[0]
                    }
                    if (i[1] === 'EUR') {
                      totals.eurTotal += i[0]
                    }
                    if (i[1] === 'USD') {
                      totals.usdTotal += i[0]
                    }
                  }
                  )

                  return (
                    <Stats
                      filter={{...this.state.filterParams, orderState: this.state.filterParams.orderState || getDefaultStatuses(orderStates)}}
                      customerId={this.props.customerId}
                      domain={this.props.domain}
                    >
                      {({ stats }: any) => {
                        if (stats.loading || !stats.stats) {
                          return null;
                        }

                        return (
                          <>
                            {stats.stats.map((stat: any, index: number) => (
                              <Table.Summary.Row key={index}>
                                <Table.Summary.Cell index={0} />
                                <Table.Summary.Cell index={1} />
                                <Table.Summary.Cell index={2} />
                                <Table.Summary.Cell index={3} />
                                <Table.Summary.Cell index={4} />
                                <Table.Summary.Cell index={5}>
                                  <b>
                                    {index === 0 ? 'Total (goods only)' : ''}
                                  </b>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6}>
                                  <b>{stat.currency}</b>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell className={'table-total'} index={7}>
                                  <b>{Number(stat.total.toFixed(2)).toLocaleString('en', {
                                    minimumFractionDigits: 2
                                  })} {stat.curency}</b>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            ))}
                          </>
                        )
                      }
                      }
                    </Stats>
                  );
                }}
              >
                <Table.Column
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <span>
                        {moment(new Date(row.createdAt))
                          .local()
                          .format('DD/MM/YY HH:mm')}
                      </span>
                    );
                  }}
                  title="Date created"
                  dataIndex="createdAt"
                  key="createdAt"
                  filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                    <div style={{ padding: 8 }}>
                      <Space>
                        <DatePicker
                          style={{ width: 135, marginBottom: 8 }}
                          onChange={e => { e && this.setFilterParam('createdAtFrom', moment(e).format('YYYY-MM-DD')) }}
                          value={this.state.filterParams.createdAtFrom ? moment(this.state.filterParams.createdAtFrom) : null}
                          placeholder={'From date'}
                          allowClear={false}
                        />
                        <DatePicker
                          style={{ width: 135, marginBottom: 8 }}
                          onChange={e => { e && this.setFilterParam('createdAtTo', moment(e).format('YYYY-MM-DD')) }}
                          value={this.state.filterParams.createdAtTo ? moment(this.state.filterParams.createdAtTo) : null}
                          placeholder={'To date'}
                          allowClear={false}
                        />
                      </Space>
                      <br />
                      <Space>
                        <Button
                          onClick={() => {
                            this.handleReset(clearFilters);
                            this.setState({
                              filterParams: {
                                ...this.state.filterParams,
                                createdAtFrom: null,
                                createdAtTo: null
                              }
                            });
                          }}
                          style={{ width: 278 }}
                        >
                          Reset
                      </Button>
                      </Space>
                    </div>
                  }
                  // onFilter={(value, row) => {
                  //   let from = this.state.filterParams.createdAtFrom ? parseInt(this.state.filterParams.createdAtFrom) : 0;
                  //   let to = this.state.filterParams.createdAtTo ? parseInt(this.state.filterParams.createdAtTo) : Infinity;
                  //   let timestamp = moment(row.createdAt).startOf('day').unix()
                  //   if (from <= timestamp && to >= timestamp) {return true}
                  //   return false
                  // }}
                  // filteredValue={
                  //   (this.state.filterParams.createdAtFrom || this.state.filterParams.createdAtTo)
                  //   && this.getUniqueData(orders, 'createdAt').filter((i: number) => {
                  //     let timestamp = moment(i).startOf('day').unix()
                  //     let from = this.state.filterParams.createdAtFrom ? parseInt(this.state.filterParams.createdAtFrom) : 0;
                  //     let to = this.state.filterParams.createdAtTo ? parseInt(this.state.filterParams.createdAtTo) : Infinity;
                  //     if (from <= timestamp && to >= timestamp) {return true}
                  //     return false
                  //   }).concat('') as string[]
                  //   || null
                  // }
                  width='10%'
                />
                <Table.Column
                  title="Order #"
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <React.Fragment>
                        {row && row.orderNumber || '-'}
                      </React.Fragment>);
                  }}
                  dataIndex={'orderNumber'}
                  key="orderNumber"
                  width='10%'
                />
                <Table.Column
                  title="Order state"
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <React.Fragment>
                        {row && row.externalState &&
                          <Tag color={row.externalState.color || ''}>{row.externalState.title}</Tag>}
                      </React.Fragment>);
                  }}
                  dataIndex={'orderState'}
                  key="orderState"
                  filters={this.getFilterData(orderStates, 'id', 'title')}
                  filteredValue={this.state.filterParams.orderState || getDefaultStatuses(orderStates)}
                  width='10%'
                />
                <Table.Column
                  title="Order paid"
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <React.Fragment>
                        {row && row.paid && <Tag color={'red'}>Order paid</Tag> || '-'}
                      </React.Fragment>);
                  }}
                  dataIndex={'orderPaid'}
                  key="orderPaid"
                  filters={[
                    {
                      text: 'Yes',
                      value: 'true'
                    },
                    {
                      text: 'No',
                      value: 'false'
                    }
                  ]}
                  // onFilter={(value, row) => String(row.paid) === value}
                  filteredValue={
                    this.state.filterParams.orderPaid
                    && (Array.isArray(this.state.filterParams.orderPaid)
                      ? this.state.filterParams.orderPaid
                      : [this.state.filterParams.orderPaid])
                  }
                  width='10%'
                />
                <Table.Column
                  title="Delivery"
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <React.Fragment>
                        <Tag>{row.deliveryMethod.title}</Tag>
                      </React.Fragment>);
                  }}
                  dataIndex={'deliveryMethod'}
                  key="deliveryMethod"
                  filters={this.getFilterData(deliveryMethods, 'id', 'title')}
                  onFilter={(value, row) => row.deliveryMethod && row.deliveryMethod.id === value || false}
                  filteredValue={
                    this.state.filterParams.deliveryMethod
                    && (Array.isArray(this.state.filterParams.deliveryMethod)
                      ? this.state.filterParams.deliveryMethod
                      : [this.state.filterParams.deliveryMethod])
                  }
                  width='15%'
                />
                <Table.Column
                  title="Payment"
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <React.Fragment>
                        <Tag>{row.paymentMethod.title}</Tag>
                      </React.Fragment>);
                  }}
                  dataIndex={'paymentMethod'}
                  key="paymentMethod"
                  filters={this.getFilterData(paymentMethods, 'id', 'title')}
                  // onFilter={(value, row) => row.paymentMethod && row.paymentMethod.title === value || false}
                  filteredValue={
                    this.state.filterParams.paymentMethod
                    && (Array.isArray(this.state.filterParams.paymentMethod)
                      ? this.state.filterParams.paymentMethod
                      : [this.state.filterParams.paymentMethod])
                  }
                  width='10%'
                />
                <Table.Column
                  title="Customer"
                  render={(u, row: GQL.IOrder) => {
                    return <span>{row.invoiceFirstName} {row.invoiceLastName}</span>;
                  }}
                  dataIndex="customer"
                  key="customer"
                  width='15%'
                />
                <Table.Column
                  title="Grand total"
                  dataIndex="grandTotal"
                  key="grandTotal"
                  align="right"
                  render={(u, row: GQL.IOrder) => {
                    return (
                      <PriceDisplay
                        amount={row.grandTotal}
                        currency={row.currency}
                      />
                    );
                  }}
                  width='10%'
                  filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                    <div style={{ padding: 8 }}>
                      <Space>
                        <Input
                          style={{ width: 90, marginBottom: 8 }}
                          type={'number'}
                          onChange={e => { e.target.value && this.setFilterParam('orderAmountFrom', e.target.value) }}
                          defaultValue={this.state.filterParams.orderAmountFrom || ''}
                          placeholder={'From'}
                        />
                        <Input
                          style={{ width: 90, marginBottom: 8 }}
                          type={'number'}
                          onChange={e => { e.target.value && this.setFilterParam('orderAmountTo', e.target.value) }}
                          defaultValue={this.state.filterParams.orderAmountTo || ''}
                          placeholder={'To'}
                        />
                      </Space>
                      <br />
                      <Space>
                        <Button
                          onClick={() => {
                            this.handleReset(clearFilters);
                            this.setState({
                              filterParams: {
                                ...this.state.filterParams,
                                orderAmountFrom: null,
                                orderAmountTo: null
                              }
                            });
                          }}
                          style={{ width: 188 }}
                        >
                          Reset
                    </Button>
                      </Space>
                    </div>

                  }
                  // onFilter={(value, row) => {
                  //   let from = this.state.filterParams.orderAmountFrom ? parseFloat(this.state.filterParams.orderAmountFrom) : 0;
                  //   let to = this.state.filterParams.orderAmountTo ? parseFloat(this.state.filterParams.orderAmountTo) : Infinity;
                  //   if (from <= row.grandTotal && to >= row.grandTotal) {return true}
                  //   return false
                  // }}
                  filteredValue={
                    (this.state.filterParams.orderAmountFrom || this.state.filterParams.orderAmountTo)
                    && this.getUniqueData(orders, 'grandTotal').filter((i: number) => {
                      let from = this.state.filterParams.orderAmountFrom ? parseFloat(this.state.filterParams.orderAmountFrom) : 0;
                      let to = this.state.filterParams.orderAmountTo ? parseFloat(this.state.filterParams.orderAmountTo) : Infinity;
                      if (from <= i && to >= i) { return true }
                      return false
                    }).concat('') as string[]
                    || null
                  }
                />
                <Table.Column
                  title="Actions"
                  dataIndex="actions"
                  key="actions"
                  render={(u: any, row: any) => this.renderActions(u, row, deleteOrder, orders.refetch)}
                />
              </Table>
            </div>
          }
        </CurrenciesQuery>

      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading orders ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(u: any, order: any, deleteCategory: Function, refetch: Function) {
    return (
      <div>
        <Button type={'primary'} onClick={() => this.setCurrentOrder(order.id)}>
          View
        </Button>
      </div>
    );
  }

  private newOrder() {
    this.props.onNewOrder();
  }

  private setCurrentOrder(orderId: string) {
    this.props.onEditOrder(orderId);
  }

  private delete(order: any, deleteOrder: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${order.title}?`,
      content: (
        <span>
          Are you sure you want to delete order <strong>{order.title}</strong>
        </span>
      ),
      onOk: () => {
        return deleteOrder({
          variables: {
            id: order.id
          }
        }).then(() => refetch());
      }
    });
  }
}

export default OrdersTable;



// WEBPACK FOOTER //
// ./src/scenes/Orders/OrdersTable.tsx